import { getTranslations } from 'next-intl/server';
import Image from 'next/image';
import { Suspense } from 'react';

import { Link } from '@/components/Link';
import VerticalLogo from '@/public/icons/verticalLogoWhiteIcon.svg';

import LanguageSection from './LanguageSection';
import LinksSection from './LinksSection';
import NewsletterSkeleton from './NewsletterSkeleton';
import NewsletterWrapper from './NewsletterWrapper';
import SocialMediaList from './SocialMediaList';

export default async function InternalFooter({ locale }: { locale: string }) {
  const t = await getTranslations({
    locale,
    namespace: 'footer',
  });

  return (
    <footer className="text-white bg-teal-40 py-14">
      <div className="base-container">
        <Suspense fallback={<NewsletterSkeleton locale={locale} />}>
          <NewsletterWrapper locale={locale} />
        </Suspense>
        <hr className="mb-6" />
        <div className="grid grid-cols-2 gap-x-5 gap-y-10 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          <LinksSection
            title={t('industrious')}
            items={[
              {
                href: '/about-us',
                dataTestId: 'link-about',
                label: t('about'),
              },
              {
                href: '/locations',
                dataTestId: 'link-locations',
                label: t('locations'),
              },
              {
                href: '/about-us#faqs',
                dataTestId: 'link-faq',
                label: t('faq'),
              },
              {
                href: '/about-us#team',
                dataTestId: 'link-team',
                label: t('team'),
              },
              { href: '/blog', dataTestId: 'link-blog', label: t('blog') },
            ]}
          />
          <LinksSection
            title={t('solutions')}
            items={[
              {
                href: '/solutions/offices',
                dataTestId: 'link-offices',
                label: t('offices'),
              },
              {
                href: '/solutions/suites',
                dataTestId: 'link-suites',
                label: t('suites'),
              },
              {
                href: '/solutions/coworking-membership',
                dataTestId: 'link-access',
                label: t('access'),
              },
              {
                href: '/solutions/virtual',
                dataTestId: 'link-virtual',
                label: t('virtual'),
              },
              {
                href: '/solutions/conference-and-meeting-rooms',
                dataTestId: 'link-meetings',
                label: t('meetings'),
              },
            ]}
          />
          <LinksSection
            title={t('partnerships')}
            items={[
              {
                href: '/brokers',
                dataTestId: 'broker',
                label: t('brokers'),
              },
              {
                href: '/enterprise',
                dataTestId: 'link-enterprise',
                label: t('enterprise'),
              },
              {
                href: '/landlords',
                dataTestId: 'link-partnerships',
                label: t('landlords'),
              },
            ]}
          />
          <div>
            <LinksSection
              title={t('connect')}
              items={[
                {
                  href: '/contact-us',
                  dataTestId: 'contact-us',
                  label: t('contact'),
                },
                {
                  href: '/contact-us?prMediaForm=open',
                  dataTestId: 'link-press',
                  label: t('press'),
                },
                {
                  href: '/careers',
                  dataTestId: 'link-careers',
                  label: t('careers'),
                },
              ]}
            />
            <SocialMediaList locale={locale} />
          </div>

          <LinksSection
            title={t('members')}
            items={[
              {
                href: 'https://portal.industriousoffice.com',
                dataTestId: 'link-signin',
                label: t('signin'),
                isExternal: true,
              },
              {
                href: 'https://apps.apple.com/us/app/industrious-office/id1571362704',
                dataTestId: 'link-download-ios',
                label: t('download-ios'),
                isExternal: true,
              },
              {
                href: 'https://play.google.com/store/apps/details?id=com.industriousoffice.app',
                dataTestId: 'link-download-android',
                label: t('download-android'),
                isExternal: true,
              },
            ]}
          />
          <LanguageSection />
        </div>
        <div className="flex flex-col items-center gap-y-8 lg:flex-row lg:items-end justify-between mt-[48px] sm:mt-[70px]">
          <Link className="pb-[5px] lg:mb-0" href="/" data-testid="footer-logo">
            <Image src={VerticalLogo} alt="Industrious Logo" width={130} />
          </Link>
          <div className="flex flex-wrap justify-center gap-5 text-sm text-center gap-y-4 lg:items-end">
            <Link
              className="hover:underline"
              href="/site-portal-terms"
              data-testid="link-terms"
            >
              {t('terms')}
            </Link>
            <Link
              className="hover:underline"
              href="/privacy-policy"
              data-testid="link-privacy"
            >
              {t('privacy')}
            </Link>
            <div data-testid="copyrights">{t('copyrights')}</div>
          </div>
        </div>
      </div>
    </footer>
  );
}
